import { create } from 'zustand';
import documentService from '../api/documentService';
import { 
  Document, 
  DocumentFilters, 
  QuotaInfo,
  UploadQueueItem,
  ProcessingStatus 
} from '../types/document';

interface DocumentStore {
  // State
  documents: Document[];
  totalDocuments: number;
  isLoading: boolean;
  error: string | null;
  filters: DocumentFilters;
  uploadQueue: UploadQueueItem[];
  userQuota: QuotaInfo | null;
  selectedDocuments: string[];
  
  // Actions
  fetchDocuments: (filters?: DocumentFilters, silent?: boolean, authHeaders?: Record<string, string>) => Promise<void>;
  uploadDocument: (file: File, metadata: any, authHeaders?: Record<string, string>) => Promise<void>;
  deleteDocument: (documentId: string, authHeaders?: Record<string, string>) => Promise<void>;
  deleteMultipleDocuments: (documentIds: string[], authHeaders?: Record<string, string>) => Promise<void>;
  updateFilters: (filters: Partial<DocumentFilters>) => void;
  fetchUserQuota: (authHeaders?: Record<string, string>) => Promise<void>;
  setSelectedDocuments: (ids: string[]) => void;
  toggleDocumentSelection: (id: string) => void;
  clearSelection: () => void;
  
  // Chat integration
  getDocumentsForChat: () => Document[];
  addDocumentToChat: (documentId: string) => void;
  removeDocumentFromChat: (documentId: string) => void;
  chatDocumentIds: string[];
  
  // Additional actions
  addDocument: (document: Document) => void;
  clearError: () => void;
  
  // Processing status updates
  updateDocumentStatus: (documentId: number, status: ProcessingStatus) => void;
  updateDocument: (documentId: number, updates: Partial<Document>) => void;
}

const useDocumentStore = create<DocumentStore>((set, get) => ({
  // Initial state
  documents: [],
  totalDocuments: 0,
  isLoading: false,
  error: null,
  filters: {
    limit: 20,
    offset: 0,
    sortBy: 'date',
    sortOrder: 'desc'
  },
  uploadQueue: [],
  userQuota: null,
  selectedDocuments: [],
  chatDocumentIds: [],

  // Fetch documents with current filters
  fetchDocuments: async (filters?: DocumentFilters, silent = false, authHeaders?: Record<string, string>) => {
    console.log('[DocumentStore] fetchDocuments called with filters:', filters, 'silent:', silent);
    
    const currentState = get();
    
    // If silent mode, don't set loading state to prevent UI flash
    if (!silent) {
      set({ isLoading: true, error: null });
    }
    
    try {
      const currentFilters = filters || currentState.filters;
      const response = authHeaders
        ? await documentService.listDocuments(currentFilters, authHeaders)
        : await documentService.listDocuments(currentFilters);
      
      console.log('[DocumentStore] Fetched documents:', response.documents.length, 'total:', response.total);
      
      // Only update if documents have changed or it's not a silent update
      const documentsChanged = JSON.stringify(response.documents) !== JSON.stringify(currentState.documents);
      
      if (documentsChanged || !silent) {
        set({
          documents: response.documents,
          totalDocuments: response.total,
          isLoading: false,
          filters: currentFilters
        });
        
        if (documentsChanged && silent) {
          console.log('[DocumentStore] Documents changed during silent update');
        }
      } else if (!silent) {
        // No changes but clear loading state if not silent
        set({ isLoading: false });
      }
    } catch (error) {
      console.error('[DocumentStore] Error fetching documents:', error);
      
      // Don't show authentication errors during silent updates (polling)
      const isAuthError = error instanceof Error && 
        (error.message.includes('Authentication') || error.message.includes('session'));
      
      if (!silent && !isAuthError) {
        set({
          error: error instanceof Error ? error.message : 'Failed to fetch documents',
          isLoading: false
        });
      } else if (!silent) {
        // For auth errors in non-silent mode, just clear loading state
        // Let AuthContext handle the authentication flow
        set({ isLoading: false });
      }
    }
  },

  // Upload document (placeholder - actual upload handled by useDocumentUpload hook)
  uploadDocument: async (file: File, metadata: any, authHeaders?: Record<string, string>) => {
    // This will be integrated with the useDocumentUpload hook
    // For now, just refresh the document list after upload
    await get().fetchDocuments(undefined, false, authHeaders);
    await get().fetchUserQuota(authHeaders);
  },

  // Delete single document
  deleteDocument: async (documentId: string, authHeaders?: Record<string, string>) => {
    set({ isLoading: true, error: null });
    
    try {
      if (authHeaders) {
        await documentService.deleteDocument(documentId, authHeaders);
      } else {
        await documentService.deleteDocument(documentId);
      }
      
      // Remove from local state
      set(state => ({
        documents: state.documents.filter(doc => doc.doc_id.toString() !== documentId),
        totalDocuments: state.totalDocuments - 1,
        selectedDocuments: state.selectedDocuments.filter(id => id !== documentId),
        chatDocumentIds: state.chatDocumentIds.filter(id => id !== documentId),
        isLoading: false
      }));
      
      // Update quota
      await get().fetchUserQuota(authHeaders);
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete document',
        isLoading: false
      });
    }
  },

  // Delete multiple documents
  deleteMultipleDocuments: async (documentIds: string[], authHeaders?: Record<string, string>) => {
    set({ isLoading: true, error: null });
    
    try {
      // Use batch delete endpoint
      const result = await documentService.batchDeleteDocuments(documentIds, authHeaders);
      
      // Get successfully deleted document IDs
      const deletedIds = result.results
        .filter(r => r.success)
        .map(r => r.document_id);
      
      // Remove successfully deleted documents from local state
      set(state => ({
        documents: state.documents.filter(
          doc => !deletedIds.includes(doc.doc_id.toString())
        ),
        totalDocuments: state.totalDocuments - deletedIds.length,
        selectedDocuments: [],
        chatDocumentIds: state.chatDocumentIds.filter(
          id => !deletedIds.includes(id)
        ),
        isLoading: false
      }));
      
      // Update quota
      await get().fetchUserQuota(authHeaders);
      
      // If some documents failed to delete, show an error
      if (result.totalFailed > 0) {
        const failedDocs = result.results.filter(r => !r.success);
        const errorMessage = `Failed to delete ${result.totalFailed} document(s). ${failedDocs[0]?.error || ''}`;
        set({ error: errorMessage });
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to delete documents',
        isLoading: false
      });
    }
  },

  // Update filters
  updateFilters: (newFilters: Partial<DocumentFilters>) => {
    const currentFilters = get().filters;
    // Reset offset when changing filters (except when changing offset itself)
    const updatedFilters = { 
      ...currentFilters, 
      ...newFilters,
      offset: newFilters.offset !== undefined ? newFilters.offset : 0
    };
    set({ filters: updatedFilters });
    get().fetchDocuments(updatedFilters, false);
  },

  // Fetch user quota
  fetchUserQuota: async (authHeaders?: Record<string, string>) => {
    try {
      const quota = await documentService.getUserQuota(authHeaders);
      set({ userQuota: quota });
    } catch (error) {
      console.error('Failed to fetch quota:', error);
    }
  },

  // Selection management
  setSelectedDocuments: (ids: string[]) => {
    set({ selectedDocuments: ids });
  },

  toggleDocumentSelection: (id: string) => {
    set(state => ({
      selectedDocuments: state.selectedDocuments.includes(id)
        ? state.selectedDocuments.filter(docId => docId !== id)
        : [...state.selectedDocuments, id]
    }));
  },

  clearSelection: () => {
    set({ selectedDocuments: [] });
  },

  // Chat integration methods
  getDocumentsForChat: () => {
    const state = get();
    return state.documents.filter(doc => 
      state.chatDocumentIds.includes(doc.doc_id.toString())
    );
  },

  addDocumentToChat: (documentId: string) => {
    set(state => ({
      chatDocumentIds: [...state.chatDocumentIds, documentId]
    }));
  },

  removeDocumentFromChat: (documentId: string) => {
    set(state => ({
      chatDocumentIds: state.chatDocumentIds.filter(id => id !== documentId)
    }));
  },

  // Add new document to the list
  addDocument: (document: Document) => {
    console.log('[DocumentStore] Adding document:', document);
    set(state => {
      // Check if document already exists to avoid duplicates
      const existingIndex = state.documents.findIndex(doc => doc.doc_id === document.doc_id);
      
      if (existingIndex >= 0) {
        console.log('[DocumentStore] Document already exists, updating it');
        // Update existing document
        const newDocuments = [...state.documents];
        newDocuments[existingIndex] = document;
        return {
          ...state,
          documents: newDocuments
        };
      }
      
      // Add new document at the beginning
      const newState = {
        documents: [document, ...state.documents],
        totalDocuments: state.totalDocuments + 1
      };
      console.log('[DocumentStore] New documents count:', newState.documents.length);
      return newState;
    });
  },

  // Clear error state
  clearError: () => {
    set({ error: null });
  },

  // Update document processing status
  updateDocumentStatus: (documentId: number, status: ProcessingStatus) => {
    set(state => ({
      documents: state.documents.map(doc =>
        doc.doc_id === documentId
          ? { ...doc, processing_status: status }
          : doc
      )
    }));
  },

  // Update document with partial updates
  updateDocument: (documentId: number, updates: Partial<Document>) => {
    console.log('[DocumentStore] Updating document:', documentId, 'with:', updates);
    set(state => {
      const updatedDocuments = state.documents.map(doc =>
        doc.doc_id === documentId
          ? { ...doc, ...updates }
          : doc
      );
      
      const updatedDoc = updatedDocuments.find(doc => doc.doc_id === documentId);
      if (updatedDoc) {
        console.log('[DocumentStore] Document updated:', {
          documentId: updatedDoc.doc_id,
          status: updatedDoc.processing_status,
          wordCount: updatedDoc.word_count,
          hasInsights: !!updatedDoc.ai_insights
        });
      }
      
      return { documents: updatedDocuments };
    });
  }
}));

export default useDocumentStore;
